// hljs will be dynamically imported
import Head from 'next/head';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next/types';
import { useCallback, useEffect, useState } from 'react';
import { ShouldBeRedirected } from '..';
import { trackArticleViewIfEligible } from '../../../backend/features/viewsTracking';
import { Article, Category } from '../../../backend/models';
import { getAllArticles, GetArticleBySlug, getArticleBySlug, getArticleSlugForAlternate } from '../../../backend/queries/articles';
import { getAllCategories } from '../../../backend/queries/categories';
import { ArticleNavigation } from '../../../components/ArticleNavigation';
import { ArticlesMenu } from '../../../components/ArticlesMenu';
import { ArticleFeedback } from '../../../components/feedback/ArticleFeedback';
import { useLightbox } from '../../../components/hooks/useLightbox';
import { SectionLayout } from '../../../components/layouts/SectionLayout';
import { KbContext } from '../../../frontend/KbContext';
import { getFileUrl } from '../../../utils/imaging';
import { removeQueryParamsFromRouter } from '../../../utils/localeHandling';
import { getCanonicalURL } from '../../../utils/seo/getCanonicalURL';
import { handleAndGetTenantAsync, PageBaseProps } from '../../../utils/tenants';
import treelizeCategories from '../../../utils/treelizeCategories';
import { renderLexicalToHTML } from '../../../utils/lexical-server';
import { Footer } from '../../_app';
import { sanitizeTenant } from '../../../utils/sanitizeTenant';
import { shouldRegenerateFromImport, regenerateAndSaveContent } from '../../../utils/content-regeneration';
import { wrapImgWithSpan } from '../../../utils/textTools';
import { generateFilteredHtml } from '../../../components/lexical/Editor/utils/htmlGeneration';


type Props = PageBaseProps & {
    allCategories: Array<Category>,
    allArticles: Array<Article>,
    articleContent: GetArticleBySlug & { renderedContent?: string; };
};

const allowedHeaders = ['H2', 'H3', 'H4', 'H5', 'H6', 'H7'];

export default function ArticlePage(props: Props) {
    const [articleDomRef, setArticleDomRef] = useState<HTMLBaseElement | null>(null);
    const router = useRouter();

    const isEmbedded = router.query.embed == '1';
    const noNav = router.query.nonav == '1';

    removeQueryParamsFromRouter(router, ['r', 'lang_selector']); // remove the "r"

    const categories = treelizeCategories(props.allCategories);

    useEffect(() => {
        import('highlight.js').then((hljs) => {
            hljs.default.highlightAll();
        });
    }, [props.articleContent.id]);

    const articleContentRef = useCallback((node: HTMLDivElement) => {
        if (node) {
            // parse headers
            try {
                const allHeaders = Array.from(node.querySelectorAll(allowedHeaders.join(',')));
                allHeaders
                    .forEach(el => {
                        el.setAttribute('id', el.textContent?.toLocaleLowerCase().replaceAll(' ', '-').replaceAll('#', '-') || '');
                        el.classList.add('hashed-header');
                    });

                attachHeadersHashButtons(allHeaders);
            } catch (e) { }

            // create lightbox
            try {
                const allImages = Array.from(node.querySelectorAll('img'));

                allImages.forEach(imgEl => {
                    imgEl.dataset.lghtb = 'true';
                });
            } catch (e) { }
        }
    }, [props.articleContent.id]);

    const LightBox = useLightbox();


    return <KbContext.Provider value={{ allArticles: props.allArticles, allCategories: props.allCategories, treelizedCategories: categories }}>
        <Head>
            <title>{`${Array.isArray(props.articleContent.title) ? props.articleContent.title.join(' ') : props.articleContent.title} - ${props.tenant.title}`}</title>
            <link rel='icon' type='image/png' href={getFileUrl(props.tenant.icons.favIconFileId)} />
            {props.canonical?.usingCustomDomain && <link rel='canonical' href={props.canonical?.canonicalUrl} />}
            <meta property="og:image" content={getFileUrl(props.tenant.icons.logoFileId)}></meta>
            {props.articleContent.isHidden && <meta name="robots" content="noindex, nofollow"></meta>}
        </Head>
        <SectionLayout isEmbed={isEmbedded}>
            <div className='mt-10'>
                <div className={`${noNav ? '' : 'content-grid'} w-full`}>
                    {!noNav && <div className="h-full menu-column border-r border-r-slate-300">
                        <div className={`fixed bottom-0 overflow-auto mb-10 z-20 ${!isEmbedded ? 'top-[7rem]' : 'top-10'}`}>
                            <ArticlesMenu categories={categories}
                                articles={props.allArticles}
                                selectedArticleContent={props.articleContent} />
                        </div>
                    </div>}
                    <div className='px-8 pr-10'>
                        <article className={`${props.articleContent.version === 'v2' ? 'article-v2' : 'article ck-content'}  shrink ${props.articleContent.version === 'v2' && props.articleContent.contentJson ? 'disable-code-styling' : ''}`}
                            ref={(r) => setArticleDomRef(r as unknown as HTMLBaseElement)}>

                            <h1 className='font-extrabold text-4xl mb-5'>{props.articleContent.title}</h1>
                            {props.articleContent.lead && <div className='mt-2 text-lg text-slate-500'>{props.articleContent.lead}</div>}

                            {props.articleContent.version === 'v2' && props.articleContent.contentJson ? (
                                <div className='prose-kb' ref={articleContentRef} dangerouslySetInnerHTML={{
                                    __html: wrapImgWithSpan(props.articleContent.content)
                                }}>
                                </div>
                                // <LexicalReadOnlyRenderer
                                //     contentJson={props.articleContent.contentJson}
                                //     fallbackHtml={props.articleContent.renderedContent || wrapImgWithSpan(props.articleContent.content)}
                                // />
                            ) : (<>
                                <div ref={articleContentRef} dangerouslySetInnerHTML={{
                                    __html: wrapImgWithSpan(props.articleContent.content)
                                }}>
                                </div>
                            </>
                            )}
                        </article>

                        {props.tenant.feedback.enabled && <ArticleFeedback
                            key={props.articleContent.id.toString()}
                            tenant={props.tenant}
                            articleId={props.articleContent.articleId}
                            articleContentId={props.articleContent.id} />}
                    </div>
                    {!noNav && <div className="border-l border--slate-300 menu-column abstract-column">
                        <div className='fixed bottom-0 top-[8rem] overflow-auto pr-4  mb-10 z-20'>
                            <ArticleNavigation articleDom={articleDomRef} contentId={props.articleContent.id} />
                        </div>
                    </div>}
                </div>
            </div>
            {LightBox}
        </SectionLayout>

        <Footer slugs={props.articleContent.slugs} />
    </KbContext.Provider>;
}



export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
    const { tenant, auth, notFound } = await handleAndGetTenantAsync(ctx, true);

    if (notFound) {
        return {
            notFound: true
        };
    }

    if (!auth.isFromAllowedIP) {
        return {
            notFound: true
        };
    }

    const isFromLangDropdown = ctx.query.lang_selector == "true";

    let locale = (ctx.params?.language ?? tenant.languagesHandling.defaultLanguage ?? 'en') as string;

    if (!auth?.isAuthorized) {
        const returnUrl = encodeURIComponent(`/${locale}${ctx?.resolvedUrl?.replace(/^\/([a-z]{2})\//, '/') ?? ""}`);

        return {
            redirect: {
                permanent: false,
                destination: `/${locale}/auth/signin/?return=${returnUrl}&protected=true`,
            },
            props: {},
        };
    }
    const result = ShouldBeRedirected(ctx, tenant);
    if (result) return result;

    const articleSlug = (ctx.query['slug'] as string[])[0];

    const cats = await getAllCategories(tenant);
    const allArticles = await getAllArticles(tenant, auth?.isAuthenticated);
    const articleContent = await getArticleBySlug(tenant.projectId, tenant.currentLocale, articleSlug, true);

    if (!articleContent) {

        if (isFromLangDropdown) {
            return {
                redirect: {
                    permanent: false,
                    destination: `/${locale}`,
                },
                props: {},
            };
        }

        const alternateSlug = await getArticleSlugForAlternate(tenant, articleSlug, locale, true);

        if (!alternateSlug) {
            return {
                notFound: true
            };
        } else {
            if (articleSlug === alternateSlug) {
                return {
                    notFound: true
                };
            }
            const { res } = ctx;
            res.setHeader('Cache-Control', 'no-store, max-age=0');

            return {
                redirect: {
                    permanent: true,
                    destination: `/${locale}/article/${alternateSlug}`,
                },
                props: {},
            };
        }
    }

    if (articleContent.isPrivate && !auth?.isAuthenticated) {
        return {
            redirect: {
                permanent: false,
                destination: `/${locale}/auth/signin/?return=/${locale}${ctx?.resolvedUrl?.replace(/^\/([a-z]{2})\//, '/') ?? ""}&protected=true`,
            },
            props: {},
        };
    }

    await trackArticleViewIfEligible(ctx, tenant.projectId, articleContent.articleId, articleContent.id);

    const canonical = getCanonicalURL(tenant, ctx.resolvedUrl);

    // Check if content needs regeneration and regenerate/save if needed
    const shouldRegenerate = shouldRegenerateFromImport(
        articleContent.originalMarkdown,
        articleContent.importedAt,
        articleContent.contentModifyImportAt,
        articleContent.lastUpdatedAt
    );


    let finalContentJson = articleContent.contentJson;
    if (shouldRegenerate && articleContent.originalMarkdown) {
        try {
            const regeneratedJson = await regenerateAndSaveContent(
                articleContent.id,
                articleContent.originalMarkdown
            );
            if (regeneratedJson) {
                finalContentJson = regeneratedJson;
            }
        } catch (error) {
            console.error('Failed to regenerate content:', error);
        }
    }

    let renderedContent: string | undefined;
    if (articleContent.version === 'v2' && finalContentJson) {
        try {
            renderedContent = await renderLexicalToHTML(finalContentJson);
            articleContent.content = renderedContent;
        } catch (error) {
            console.error('Failed to pre-render Lexical content:', error);
        }
    }

    articleContent.content = articleContent?.version === 'v2' ? generateFilteredHtml(articleContent.content, 'include-user') : articleContent.content;

    return {
        props: {
            allCategories: cats,
            allArticles: allArticles,
            articleContent: { ...articleContent, contentJson: finalContentJson, renderedContent },
            tenant: sanitizeTenant(tenant),
            auth: auth,
            canonical
        }
    };
};

let hashBtnInitialized = false;
function attachHeadersHashButtons(headers: Array<Element>) {
    let hashBtn: HTMLElement = document.createElement('div');

    let currentHeaderId = '';

    if (!hashBtnInitialized || !document.contains(hashBtn)) {
        hashBtn.id = 'hash-btn';
        hashBtn.textContent = '#';
        hashBtn.addEventListener('click', () => {
            window.location.hash = currentHeaderId;
        });

        hashBtnInitialized = true;
    }

    for (const header of headers) {
        header.addEventListener('mouseenter', (e) => {
            const target = e.target as HTMLElement;

            if (target && target.classList && target.classList.contains('hashed-header')) {
                hashBtn.style.top = '2px';
                hashBtn.style.left = '-40px';
                hashBtn.style.display = 'flex';

                currentHeaderId = target.id;
                target.appendChild(hashBtn);
            }
        }, { passive: true });

        header.addEventListener('mouseleave', (e) => {
            hashBtn.style.display = 'none';
            hashBtn.remove();
            currentHeaderId = '';
        });
    }
}
